/**
 * Entry point of the React application.
 *
 * This file sets up the root of the React application and renders the main component tree.
 * It includes several providers to manage global state and context throughout the app.
 *
 * Providers included:
 * - `Provider` from `react-redux` to integrate Redux store.
 * - `AuthProvider` from `./context/AuthContext` to manage authentication state.
 * - `ThemeProvider` from `./context/ThemeContext` to manage theme state.
 * - `BackgroundProvider` from `./context/BackgroundContext` (via WrappedApp) to manage background designs.
 *
 * The `App` component (via `WrappedApp`) is wrapped with these providers to ensure that the entire application
 * has access to the Redux store, authentication context, theme context, and background context.
 *
 * @file This file is the main entry point of the React application.
 * @module main
 */
import "./utils/logger"; // This should be the first import to override console
import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import store from "./store/store";
import WrappedApp from "./App";
import { AuthProvider } from "./context/AuthContext";
import "./index.css";
import { createTheme, ThemeProvider as MuiThemeProvider, responsiveFontSizes } from "@mui/material";
import "./styles/global.scss";
import { Amplify } from "aws-amplify";
import { ThemeProvider as CustomThemeProvider, useTheme } from "./context/ThemeContext";
import ReactGA from "react-ga4";
// import { AuthProvider as CognitoAuthProvider } from "react-oidc-context";

// const cognitoAuthConfig = {
//   authority: "https://cognito-idp.ca-central-1.amazonaws.com/ca-central-1_YUw7vq2Bt",
//   client_id: "5780lphufqneo41i55t5obpeb1",
//   redirect_uri: "http://localhost:5173/setup",
//   response_type: "code",
//   scope: "email openid profile",
// };

// TODO: since we're not using amplify cognito, we should probably remove this. we're using aws sdk for cognito in signup/login.
Amplify.configure({
    Auth: {
        Cognito: {
            userPoolId: "ca-central-1_YUw7vq2Bt",
            userPoolClientId: "32bi7kch7qe0fcrkst5l2gorqa",
            identityPoolId: "ca-central-1:f68b09d9-6d2a-46c3-bf16-956c3917228f",
            // clientSecret: "lr0otpkhmhb4ccc2posfg9rtcor91rct8l0ka99d5q5435hi3br",
            loginWith: {
                email: true,
            },
            signUpVerificationMethod: "code",
            userAttributes: {
                email: {
                    required: true,
                },
            },
            allowGuestAccess: true,
            passwordFormat: {
                minLength: 8,
                requireLowercase: true,
                requireUppercase: true,
                requireNumbers: true,
                requireSpecialCharacters: true,
            },
        },
    },
});

// Import fonts

// Mondtserrat
// Supports weights 100-900
import "@fontsource-variable/montserrat";

// Roboto
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";

// Poppins
import "@fontsource/poppins/400.css";
import "@fontsource/poppins/500.css";
import "@fontsource/poppins/600.css";
import "@fontsource/poppins/700.css";

// Initialize Google Analytics
ReactGA.initialize("G-D2C77ZEYBF"); // Replace with your actual GA4 measurement ID

// Create a wrapper component that subscribes to the theme context
const ThemedApp = () => {
    const { isDarkMode } = useTheme();

    // Track page views
    React.useEffect(() => {
        ReactGA.send({ hitType: "pageview", page: window.location.pathname });
    }, []);

    // Create theme based on current mode
    const theme = React.useMemo(() => {
        let newTheme = createTheme({
            palette: {
                mode: isDarkMode ? "dark" : "light",
                primary: {
                    main: isDarkMode ? "#b7a97f" : "#9f8f5e", // Slightly lighter in dark mode
                    light: isDarkMode ? "#cfc4a5" : "#b7a97f",
                    dark: isDarkMode ? "#867541" : "#7a6a3c",
                    contrastText: "#fff",
                },
                background: {
                    default: isDarkMode ? "#24263a" : "#f1f1f1",
                    paper: isDarkMode ? "#1d2939" : "#ffffff",
                },
                text: {
                    primary: isDarkMode ? "#f9fafb" : "#101828",
                    secondary: isDarkMode ? "#d0d5dd" : "#667085",
                },
            },
            typography: {
                fontFamily: "Roboto, sans-serif",
            },
            breakpoints: {
                values: {
                    xs: 0, // Extra small devices (phones)
                    sm: 600, // Custom small breakpoint
                    md: 900, // Medium devices (tablets)
                    lg: 1200, // Large devices (laptops)
                    xl: 1536, // Extra large devices (desktops)
                },
            },
        });

        // Make fonts responsive
        newTheme = responsiveFontSizes(newTheme);

        return newTheme;
    }, [isDarkMode]);

    return (
        <MuiThemeProvider theme={theme}>
            <AuthProvider>
                <WrappedApp />
            </AuthProvider>
        </MuiThemeProvider>
    );
};

const root = ReactDOM.createRoot(document.getElementById("root")!);

// Render the app with all providers
root.render(
    <React.StrictMode>
        <Provider store={store}>
            <CustomThemeProvider>
                <ThemedApp />
            </CustomThemeProvider>
        </Provider>
    </React.StrictMode>,
);
